"use client"

import {
  ArrowUp,
  FileText,
  FileCode2,
  ImageUp,
  CirclePlus,
  Workflow,
  type LucideIcon,
  AppWindow,
} from "lucide-react"
import { useState } from "react"

import { FlowRuleDialog } from "@/modules/flow/components/flow-rule-dialog"
import { getFlowRuleDialogOptions } from "@/modules/flow/components/flow-rule-dialog-options"
import { ImportBookmarks } from "@/modules/mine/components/bookmark-import"
import { AddImage } from "@/modules/mine/components/items/add-image"
import { AddLink } from "@/modules/mine/components/items/add-link"
import { AddTextOverlay } from "@/modules/mine/components/items/add-text"
import type { TextContentMode } from "@/modules/mine/text-content"
import { useDialog } from "@/shared/components/dialog"
import { useRouteInfo } from "@/shared/hooks/use-route-info"
import { Button } from "@/shared/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu"

/** 添加操作类型 */
type ActionType = 'website' | 'text' | 'markdown' | 'image' | 'flow' | 'import'

/** 菜单项接口 */
interface ActionMenuItem {
  label: string
  icon: LucideIcon
  actionType: ActionType
}

/** 添加操作菜单分组配置 */
const ACTION_MENU_GROUPS: ActionMenuItem[][] = [
  // 基础添加操作
  [
    {
      label: "添加网页",
      icon: AppWindow,
      actionType: 'website',
    },
    {
      label: "添加文本",
      icon: FileText,
      actionType: 'text',
    },
    {
      label: "上传图片",
      icon: ImageUp,
      actionType: 'image',
    },
  ],
  // 高级操作
  [
    {
      label: "添加 Markdown（测试）",
      icon: FileCode2,
      actionType: 'markdown',
    },
    {
      label: "添加流程",
      icon: Workflow,
      actionType: 'flow',
    },
  ],
  // 批量操作
  [
    {
      label: "导入书签",
      icon: ArrowUp,
      actionType: 'import',
    },
  ],
]

/**
 * 添加操作菜单组件
 * 提供各种添加内容的快捷入口
 */
export function AddActionMenu() {
  const dialog = useDialog()
  const routeInfo = useRouteInfo();
  const [textOverlayOpen, setTextOverlayOpen] = useState(false)
  const [textContentMode, setTextContentMode] = useState<TextContentMode>("tiptap-json")


  /**
   * 处理菜单项点击事件
   * @param menuItem 被点击的菜单项
   */
  const handleMenuItemClick = (menuItem: ActionMenuItem) => {
    switch (menuItem.actionType) {
      case 'website':
        void dialog.open(AddLink, { routeInfo }, {
          title: "添加链接",
          description: "粘贴网址，我们将自动解析内容。",
        })
        break
      case 'text':
        setTextContentMode("tiptap-json")
        setTextOverlayOpen(true)
        break
      case 'markdown':
        setTextContentMode("markdown")
        setTextOverlayOpen(true)
        break
      case 'image':
        void dialog.open(AddImage, { routeInfo }, {
          title: "上传图片",
          className: 'w-full !max-w-4xl',
        })
        break
      case 'flow':
        void dialog.open(FlowRuleDialog, {}, getFlowRuleDialogOptions())
        break
      case 'import':
        void dialog.open(ImportBookmarks, {}, {
          title: "导入书签",
          description: "上传浏览器导出的 HTML 书签文件，系统会自动创建文件夹并导入网页收藏。",
          className: "w-full !max-w-xl",
          closeOnOutside: false,
        })
        break
      default:
        void dialog.alert({
          title: "功能暂未开放",
          description: "流程内容入口还未接入，后续会根据业务设计补齐。",
          confirmText: "知道了",
        })
    }
  }

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button>
            <CirclePlus />
            添加
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {ACTION_MENU_GROUPS.map((group, groupIndex) => (
            <DropdownMenuGroup key={`group-${groupIndex}`}>
              {group.map((menuItem, itemIndex) => (
                <DropdownMenuItem
                  key={`${groupIndex}-${menuItem.actionType}-${itemIndex}`}
                  onClick={() => handleMenuItemClick(menuItem)}
                >
                  <menuItem.icon />
                  {menuItem.label}
                </DropdownMenuItem>
              ))}
              {groupIndex < ACTION_MENU_GROUPS.length - 1 && (
                <DropdownMenuSeparator />
              )}
            </DropdownMenuGroup>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AddTextOverlay
        initialContentMode={textContentMode}
        open={textOverlayOpen}
        onOpenChange={setTextOverlayOpen}
        routeInfo={routeInfo}
      />
    </>
  )
}
